import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { supabase } from '../../lib/supabaseClient';

const AdminHeader = ({ homePath = '/openplay/tools' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [isAdminAuthed, setIsAdminAuthed] = useState(false);

  useEffect(() => {
    let mounted = true;

    const sync = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setIsAdminAuthed(!!data?.session?.user);
      } catch (e) {
        if (!mounted) return;
        setIsAdminAuthed(false);
      }
    };

    sync();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      sync();
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const navigationItems = [
    {
      label: '도구',
      path: '/session-list-dashboard',
      icon: 'Calendar',
      tooltip: '도구'
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleLogout = () => {
    const run = async () => {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        // ignore
      }
      navigate('/');
      setIsMobileMenuOpen(false);
    };

    run();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-card shadow-md z-100">
        <div className="h-full px-4 lg:px-6 flex items-center justify-between">
          <Link 
            to={homePath}
            className="flex items-center gap-3 transition-athletic hover:opacity-80"
          >
            <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
              <img
                src="/assets/images/picklaball-icon.png"
                alt="Pickling"
                className="w-7 h-7 object-contain"
                loading="eager"
              />
            </div>
            <span className="text-xl font-heading font-semibold text-foreground">
              Pickling
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {isAdminAuthed ? (
              <>
                {navigationItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-md
                      transition-athletic hover-lift
                      ${isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                      }
                    `}
                    title={item?.tooltip}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-medium">{item?.label}</span>
                  </Link>
                ))}
                
                <Button
                  variant="ghost"
                  size="default"
                  iconName="LogOut"
                  iconPosition="left"
                  onClick={handleLogout}
                  className="ml-2"
                >
                  로그아웃
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="default"
                iconName="Shield"
                iconPosition="left"
                onClick={() => navigate('/admin-login')}
              >
                관리자
              </Button>
            )}
          </nav>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-athletic active-press"
            aria-label="모바일 메뉴 열기/닫기"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </header>
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-background z-150 lg:hidden"
            onClick={toggleMobileMenu}
            aria-hidden="true"
          />
          
          <div className="fixed top-16 right-0 bottom-0 w-64 bg-card shadow-2xl z-200 lg:hidden overflow-y-auto">
            <nav className="flex flex-col p-4 gap-2">
              {isAdminAuthed ? (
                <>
                  {navigationItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-md
                        transition-athletic active-press
                        ${isActivePath(item?.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon name={item?.icon} size={20} />
                      <span className="font-medium">{item?.label}</span>
                    </Link>
                  ))}
                  
                  <div className="mt-4 pt-4 border-t border-border">
                    <Button
                      variant="ghost"
                      size="default"
                      iconName="LogOut"
                      iconPosition="left"
                      onClick={handleLogout}
                      fullWidth
                    >
                      로그아웃
                    </Button>
                  </div>
                </>
              ) : (
                <div className="mt-2">
                  <Button
                    variant="default"
                    size="default"
                    iconName="Shield"
                    iconPosition="left"
                    onClick={() => {
                      navigate('/admin-login');
                      setIsMobileMenuOpen(false);
                    }}
                    fullWidth
                  >
                    관리자
                  </Button>
                </div>
              )}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default AdminHeader;